import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { 
  DollarSign, 
  TrendingUp,
  GraduationCap,
  BarChart3,
  RefreshCw,
  Download,
  BookOpen
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProgramsInvestmentTab() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("executiveToken") || localStorage.getItem("chroToken") || localStorage.getItem("authToken")}` }
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/executive-enhanced/programs-investment`, getAuthHeaders());
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error("Error fetching programs data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading || !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#C4A265]" />
      </div>
    );
  }

  const { budget, programs, totalEnrollments } = data;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const regionNames = {
    jamaica: { name: 'Jamaica', flag: '🇯🇲' },
    usa: { name: 'USA', flag: '🇺🇸' },
    canada: { name: 'Canada', flag: '🇨🇦' },
    barbados: { name: 'Barbados', flag: '🇧🇧' },
    trinidad_tobago: { name: 'T&T', flag: '🇹🇹' },
    panama: { name: 'Panama', flag: '🇵🇦' }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#1a3a3a]">Development Programs - Organization Overview</h2>
          <p className="text-sm text-muted-foreground">Training investment and program enrollment tracking</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      {/* Budget Summary */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm col-span-2 bg-gradient-to-br from-[#1a3a3a] to-[#2a4a4a] text-white">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-white/20 rounded-lg">
                <DollarSign className="w-6 h-6" />
              </div>
              <div>
                <p className="text-sm text-white/70">Total Training Budget</p>
                <p className="text-3xl font-bold">{formatCurrency(budget.total)}</p>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-white/70">Utilized</span>
                <span className="font-semibold">{formatCurrency(budget.utilized)} ({budget.utilizationPercentage}%)</span>
              </div>
              <Progress value={budget.utilizationPercentage} className="h-3 bg-white/20" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <GraduationCap className="w-5 h-5 text-[#7C3AED]" />
              <span className="text-sm font-medium text-muted-foreground">Total Enrollments</span>
            </div>
            <p className="text-3xl font-bold text-[#7C3AED]">{totalEnrollments}</p>
            <p className="text-sm text-muted-foreground">across all programs</p>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-muted-foreground">Active Programs</span>
            </div>
            <p className="text-3xl font-bold text-blue-600">{programs.length}</p>
            <p className="text-sm text-muted-foreground">development tracks</p>
          </CardContent>
        </Card>
      </div>

      {/* Programs Table */}
      <Card className="border-0 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-[#C4A265]" />
            Program Enrollments by Region
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-3 text-sm font-semibold">Program</th>
                  <th className="text-center p-3 text-sm font-semibold">🇯🇲 JA</th>
                  <th className="text-center p-3 text-sm font-semibold">🇺🇸 US</th>
                  <th className="text-center p-3 text-sm font-semibold">🇨🇦 CA</th>
                  <th className="text-center p-3 text-sm font-semibold">🇧🇧 BB</th>
                  <th className="text-center p-3 text-sm font-semibold">🇹🇹 T&T</th>
                  <th className="text-center p-3 text-sm font-semibold">🇵🇦 PA</th>
                  <th className="text-center p-3 text-sm font-semibold">Total</th>
                  <th className="text-right p-3 text-sm font-semibold">Budget</th>
                </tr>
              </thead>
              <tbody>
                {programs.map((program, i) => {
                  const total = Object.values(program.regions).reduce((a, b) => a + b, 0);
                  
                  return (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <GraduationCap className="w-4 h-4 text-[#7C3AED]" />
                          <span className="font-medium">{program.name}</span>
                        </div>
                      </td>
                      <td className="p-3 text-center">
                        {program.regions.jamaica > 0 ? (
                          <Badge className="bg-blue-100 text-blue-700">{program.regions.jamaica}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {program.regions.usa > 0 ? (
                          <Badge className="bg-emerald-100 text-emerald-700">{program.regions.usa}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {program.regions.canada > 0 ? (
                          <Badge className="bg-purple-100 text-purple-700">{program.regions.canada}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {program.regions.southern_caribbean > 0 || program.regions.barbados > 0 ? (
                          <Badge className="bg-amber-100 text-amber-700">{(program.regions.barbados || 0) + (program.regions.southern_caribbean || 0)}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {(program.regions.trinidad_tobago || 0) > 0 ? (
                          <Badge className="bg-rose-100 text-rose-700">{program.regions.trinidad_tobago}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center">
                        {(program.regions.panama || 0) > 0 ? (
                          <Badge className="bg-red-100 text-red-700">{program.regions.panama}</Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>
                      <td className="p-3 text-center font-bold">{total}</td>
                      <td className="p-3 text-right text-muted-foreground">{formatCurrency(program.budget)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-semibold">
                  <td className="p-3">Total</td>
                  <td className="p-3 text-center">
                    {programs.reduce((sum, p) => sum + (p.regions.jamaica || 0), 0)}
                  </td>
                  <td className="p-3 text-center">
                    {programs.reduce((sum, p) => sum + (p.regions.usa || 0), 0)}
                  </td>
                  <td className="p-3 text-center">
                    {programs.reduce((sum, p) => sum + (p.regions.canada || 0), 0)}
                  </td>
                  <td className="p-3 text-center">
                    {programs.reduce((sum, p) => sum + (p.regions.barbados || 0) + (p.regions.southern_caribbean || 0), 0)}
                  </td>
                  <td className="p-3 text-center">
                    {programs.reduce((sum, p) => sum + (p.regions.trinidad_tobago || 0), 0)}
                  </td>
                  <td className="p-3 text-center">
                    {programs.reduce((sum, p) => sum + (p.regions.panama || 0), 0)}
                  </td>
                  <td className="p-3 text-center">{totalEnrollments}</td>
                  <td className="p-3 text-right">{formatCurrency(budget.utilized)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ROI Insight */}
      <Card className="border-0 shadow-sm bg-gradient-to-r from-slate-50 to-slate-100">
        <CardContent className="pt-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-slate-200 rounded-full">
              <TrendingUp className="w-6 h-6 text-slate-500" />
            </div>
            <div>
              <p className="font-semibold text-slate-700">Investment ROI Projection</p>
              <p className="text-sm text-muted-foreground">
                {totalEnrollments > 0 
                  ? `Based on current enrollment rates, projected skill improvement across the organization is estimated at +18% average BRI increase over 12 months.`
                  : `ROI projections will be available once employees are enrolled in development programs.`
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
